import { z } from 'zod';
import { cents, hexColor, optionalCents, optionalText, paginationQuery, requiredText, quantity } from './common';
import { PAYMENT_METHOD_TYPES } from '../settings';
import { deliveryAddressSchema } from './orders';

export const catalogBannerSchema = z.object({
  id: z.string().min(1).max(40),
  imageUrl: z.string().url().max(1000),
  mediaId: z.string().uuid().nullish(),
  title: optionalText(80),
  link: optionalText(300),
});
export type CatalogBanner = z.infer<typeof catalogBannerSchema>;

export const businessHoursSchema = z.array(
  z.object({
    weekday: z.number().int().min(0).max(6),
    open: z.string().regex(/^\d{2}:\d{2}$/),
    close: z.string().regex(/^\d{2}:\d{2}$/),
  }),
);

export const catalogSettingsUpdateSchema = z
  .object({
    slug: z
      .string()
      .trim()
      .toLowerCase()
      .min(3, 'Mínimo de 3 caracteres')
      .max(48)
      .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])$/, 'Use letras minúsculas, números e hífen'),
    isPublished: z.boolean(),
    theme: z.enum(['classic', 'modern']),
    primaryColor: hexColor,
    accentColor: hexColor,
    logoUrl: z.string().url().max(1000).nullish(),
    banners: z.array(catalogBannerSchema).max(8),
    headline: optionalText(120),
    description: optionalText(1000),
    whatsapp: optionalText(30),
    phone: optionalText(30),
    email: z.string().trim().email().max(254).nullish().or(z.literal('').transform(() => null)),
    showAddress: z.boolean(),
    instagram: optionalText(120),
    facebook: optionalText(200),
    tiktok: optionalText(120),
    website: optionalText(200),
    showPrices: z.boolean(),
    showOutOfStock: z.boolean(),
    showSearch: z.boolean(),
    showCategories: z.boolean(),
    showFeatured: z.boolean(),
    defaultView: z.enum(['grid', 'list']),
    defaultSort: z.enum(['featured', 'recent', 'price_asc', 'price_desc', 'name', 'best_sellers']),
    allowOrders: z.boolean(),
    minOrderCents: optionalCents,
    orderMessage: optionalText(500),
    requireAddress: z.boolean(),
    acceptedPaymentMethods: z.array(z.enum(PAYMENT_METHOD_TYPES)).max(10),
    businessHours: businessHoursSchema,
    closedMessage: optionalText(200),
    customDomain: z
      .string()
      .trim()
      .toLowerCase()
      .regex(/^(?=.{4,253}$)([a-z0-9](-?[a-z0-9])*\.)+[a-z]{2,}$/, 'Domínio inválido')
      .nullish()
      .or(z.literal('').transform(() => null)),
    seoTitle: optionalText(70),
    seoDescription: optionalText(160),
    metaPixelId: z.string().regex(/^\d{5,20}$/, 'ID do Pixel inválido').nullish().or(z.literal('').transform(() => null)),
    gaMeasurementId: z.string().regex(/^G-[A-Z0-9]{4,15}$/, 'ID do GA4 inválido (G-XXXX)').nullish().or(z.literal('').transform(() => null)),
    privacyPolicy: optionalText(20000),
  })
  .partial();
export type CatalogSettingsUpdateInput = z.infer<typeof catalogSettingsUpdateSchema>;

// ---------------------------------------------------------------- Público
export const publicProductsQuery = paginationQuery.extend({
  q: z.string().trim().max(100).optional(),
  category: z.string().max(60).optional(),
  featured: z.coerce.boolean().optional(),
  sort: z.enum(['featured', 'recent', 'price_asc', 'price_desc', 'name', 'best_sellers']).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).default(24),
});

export const checkoutSchema = z.object({
  customer: z.object({
    name: requiredText(120, 'Informe seu nome'),
    phone: requiredText(30, 'Informe seu WhatsApp'),
    email: z.string().trim().toLowerCase().email().max(254).nullish().or(z.literal('').transform(() => null)),
    document: optionalText(20),
  }),
  items: z
    .array(
      z.object({
        productId: z.string().uuid(),
        variantId: z.string().uuid().nullish(),
        quantity,
        notes: optionalText(200),
      }),
    )
    .min(1)
    .max(200),
  deliveryType: z.enum(['pickup', 'delivery', 'shipping']),
  address: deliveryAddressSchema.nullish(),
  /**
   * Opção de frete escolhida na cotação por CEP (ex.: "pickup", "local", "pac", "sedex").
   * O servidor sempre recalcula o valor; o número que veio do navegador é ignorado.
   */
  shippingOptionId: z.string().trim().max(40).optional(),
  paymentMethod: z.enum(PAYMENT_METHOD_TYPES),
  /**
   * Pagar agora pelo site (Pix, cartão ou boleto pela conta da loja no Mercado Pago/Asaas).
   * Falso = pagar na entrega/retirada ou combinar pelo WhatsApp (comportamento antigo).
   */
  payOnline: z.boolean().default(false),
  changeForCents: optionalCents,
  couponCode: optionalText(40),
  notes: optionalText(500),
  /** Consentimento LGPD para contato de marketing. */
  marketingConsent: z.boolean().default(false),
  /** Honeypot anti-bot: deve vir vazio. */
  website: z.string().max(0).optional(),
});
export type CheckoutInput = z.infer<typeof checkoutSchema>;

/** Cotação de frete por CEP (página do produto, carrinho e checkout do catálogo). */
export const shippingQuoteSchema = z.object({
  zipCode: z.string().trim().regex(/^\d{5}-?\d{3}$/, 'CEP inválido. Informe os 8 números.'),
  /** Número do endereço (opcional): melhora a distância da entrega local. */
  number: optionalText(20),
  items: checkoutSchema.shape.items,
});
export type ShippingQuoteInput = z.infer<typeof shippingQuoteSchema>;

export const publicCouponSchema = z.object({
  code: z.string().trim().min(1).max(40),
  items: checkoutSchema.shape.items,
  customerPhone: optionalText(30),
  deliveryFeeCents: cents.default(0),
});

export const deliveryQuoteSchema = z.object({
  deliveryType: z.enum(['pickup', 'delivery', 'shipping']),
  zoneId: optionalText(40),
  subtotalCents: cents,
});
